import React, { useRef, useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import DOMPurify from 'dompurify';
import {
    getMentionQueryMatch,
    getRoomMentionQueryMatch,
    hydrateMentionPills,
    hydrateRoomMentionPills,
    resolveCaretTextPosition,
} from '../../utils/mentionUtils';
import {domToPlainText, getPlainTextSelection, plainOffsetToDomPosition} from '../../utils/composerDom';
import {convertPaste, looksLikeSpreadsheetHtml, normalizeLineEndings} from '../../utils/pasteConversion';

export interface PlainTableInsertedInfo {
    prefix: string;
    insertedText: string;
    suffix: string;
    originalText: string;
}

export interface RoomMentionTarget {
    roomId: string;
    name: string;
}

interface MentionTextInputProps {
    value: string;
    onChange: (e: { target: { value: string } }) => void;
    onKeyUp?: (e: React.KeyboardEvent<HTMLDivElement> & { target: { value: string } }) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
    placeholder?: string;
    disabled?: boolean;
    roomUsers?: { userId: string; displayName: string }[];
    hydrateMentions?: { userId: string; displayName: string }[];
    hydrateRoomMentions?: RoomMentionTarget[];
    onMentionTrigger?: (match: RegExpMatchArray | null) => void;
    onRoomMentionTrigger?: (match: RegExpMatchArray | null) => void;
    onMentionsChange?: (mentions: { userId: string; displayName: string }[]) => void;
    onRoomMentionsChange?: (roomMentions: RoomMentionTarget[]) => void;
    onPasteFiles?: (files: File[]) => void;
    onSelectionChange?: (offsets: { start: number; end: number } | null) => void;
    onPlainTableInserted?: (info: PlainTableInsertedInfo) => void;
    codeBlockOpen?: boolean;
}

export interface MentionTextInputRef {
    insertMention: (user: { userId: string; displayName: string }) => void;
    insertRoomMention: (room: RoomMentionTarget) => void;
    focus: () => void;
    getSelectionOffsets: () => { start: number; end: number } | null;
    applyTextChange: (text: string, caretOffset: number) => void;
}

const MentionTextInput = forwardRef<MentionTextInputRef, MentionTextInputProps>(({
                                                                                     value,
                                                                                     onChange,
                                                                                     onKeyUp,
                                                                                     onKeyDown,
                                                                                     placeholder,
                                                                                     disabled,
                                                                                     roomUsers = [],
                                                                                     hydrateMentions = [],
                                                                                     hydrateRoomMentions = [],
                                                                                     onMentionTrigger,
                                                                                     onRoomMentionTrigger,
                                                                                     onMentionsChange,
                                                                                     onRoomMentionsChange,
                                                                                     onPasteFiles,
                                                                                     onSelectionChange,
                                                                                     onPlainTableInserted,
                                                                                     codeBlockOpen
                                                                                 }, ref) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const [isComposing, setIsComposing] = useState(false);
    const isUserInputRef = useRef(false);
    const forcePlainPasteRef = useRef(false);

    const textToHtml = useCallback((text: string): string => {
        if (!text) return '';

        const escaped = text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');

        let withMentions: string;
        if (hydrateMentions.length > 0) {
            withMentions = hydrateMentionPills(escaped, hydrateMentions);
        } else {
            const mentionRegex = /@([^@\s]+(?:\s+[^@\s]+)*)/g;
            withMentions = escaped.replace(mentionRegex, (match: string, name: string) => {
                const user = roomUsers.find(u => {
                    const displayName = u?.displayName || `${(u as any)?.firstName || ''} ${(u as any)?.lastName || ''}`.trim();
                    return displayName.toLowerCase() === name.toLowerCase();
                });

                if (user) {
                    return `<span class="mention" data-user-id="${user.userId}" style="background-color: #e3f2fd; color: #1565c0; padding: 2px 4px; border-radius: 4px; font-weight: 500;">${match}</span>`;
                }
                return match;
            });
        }

        const withRoomMentions = hydrateRoomMentions.length > 0
            ? hydrateRoomMentionPills(withMentions, hydrateRoomMentions)
            : withMentions;

        const html = withRoomMentions.replace(/\n/g, '<br>');
        return text.endsWith('\n') ? `${html}<br>` : html;
    }, [roomUsers, hydrateMentions, hydrateRoomMentions]);

    const htmlToText = useCallback((html: string): string => {
        if (!html) return '';

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = DOMPurify.sanitize(html);

        return domToPlainText(tempDiv);
    }, []);

    const cleanupEditor = useCallback(() => {
        if (!editorRef.current) return;

        const mentions = editorRef.current.querySelectorAll('.mention');
        mentions.forEach(mention => {
            const text = mention.textContent || '';
            // Room pills read "#Name", user pills "@Name" — keeping only the
            // latter would unwrap every room link the moment it was typed.
            const hasSigil = text.startsWith('@') || text.startsWith('#');
            if (!text.trim() || !hasSigil || text.length < 2) {
                mention.outerHTML = text;
            }
        });

        if (!editorRef.current.textContent?.trim()) {
            editorRef.current.innerHTML = DOMPurify.sanitize('');
        }
    }, []);

    const ensureCleanCursor = useCallback(() => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0 || !editorRef.current) return;

        const range = selection.getRangeAt(0);
        const container = range.startContainer;
        const parent = container.parentNode as HTMLElement;

        if (parent?.classList?.contains('mention')) {
            const newRange = document.createRange();
            newRange.setStartAfter(parent);
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);

            if (!parent.nextSibling || parent.nextSibling.nodeType !== Node.TEXT_NODE) {
                const textNode = document.createTextNode('');
                parent.parentNode?.insertBefore(textNode, parent.nextSibling);
                newRange.setStart(textNode, 0);
                selection.removeAllRanges();
                selection.addRange(newRange);
            }
        }
    }, []);

    const createMentionSpan = (user: { userId: string; displayName: string }) => {
        const mentionSpan = document.createElement('span');
        mentionSpan.className = 'mention';
        mentionSpan.style.backgroundColor = '#E0E7FF';
        mentionSpan.style.color = '#432DD7';
        mentionSpan.style.padding = '2px 4px';
        mentionSpan.style.borderRadius = '4px';
        mentionSpan.style.fontWeight = '500';
        mentionSpan.setAttribute('data-user-id', user.userId);
        mentionSpan.textContent = `@${user.displayName}`;
        mentionSpan.setAttribute('contenteditable', 'false');

        return mentionSpan;
    }

    const createRoomMentionSpan = (room: RoomMentionTarget) => {
        const roomSpan = document.createElement('span');
        roomSpan.className = 'mention';
        roomSpan.style.backgroundColor = '#E0E7FF';
        roomSpan.style.color = '#432DD7';
        roomSpan.style.padding = '2px 4px';
        roomSpan.style.borderRadius = '4px';
        roomSpan.style.fontWeight = '500';
        roomSpan.setAttribute('data-room-id', room.roomId);
        roomSpan.textContent = `#${room.name}`;
        roomSpan.setAttribute('contenteditable', 'false');

        return roomSpan;
    }

    const extractRoomMentions = useCallback((): RoomMentionTarget[] => {
        if (!editorRef.current) return [];

        const roomMentions: RoomMentionTarget[] = [];
        const roomSpans = editorRef.current.querySelectorAll('.mention[data-room-id]');

        roomSpans.forEach(span => {
            const roomId = span.getAttribute('data-room-id');
            const name = span.textContent?.replace(/^#/, '') || '';
            if (roomId && name) {
                roomMentions.push({roomId, name});
            }
        });

        return roomMentions;
    }, []);

    const extractMentions = useCallback(() => {
        if (!editorRef.current) return [];

        const mentions: { userId: string; displayName: string }[] = [];
        const mentionSpans = editorRef.current.querySelectorAll('.mention[data-user-id]');

        mentionSpans.forEach(span => {
            const userId = span.getAttribute('data-user-id');
            const displayName = span.textContent?.replace('@', '') || '';
            if (userId && displayName) {
                mentions.push({ userId, displayName });
            }
        });

        return mentions;
    }, []);

    const updateMentions = useCallback(() => {
        const mentions = extractMentions();
        if (onMentionsChange) {
            onMentionsChange(mentions);
        }

        const roomMentions = extractRoomMentions();
        if (onRoomMentionsChange) {
            onRoomMentionsChange(roomMentions);
        }
    }, [extractMentions, extractRoomMentions, onMentionsChange, onRoomMentionsChange]);

    const insertMention = useCallback((user: { userId: string; displayName: string }) => {
        if (!editorRef.current) return;

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        const caretPosition = resolveCaretTextPosition(range.startContainer, range.startOffset);
        const mentionMatch = getMentionQueryMatch(caretPosition);

        if (mentionMatch && caretPosition) {
            const startPos = caretPosition.offset - mentionMatch[0].length;
            const textNode = caretPosition.node;

            const replaceRange = document.createRange();
            replaceRange.setStart(textNode, startPos);
            replaceRange.setEnd(textNode, caretPosition.offset);

            replaceRange.deleteContents();

            const mentionSpan = createMentionSpan(user);

            replaceRange.insertNode(mentionSpan);

            const spaceNode = document.createTextNode(' ');
            mentionSpan.parentNode?.insertBefore(spaceNode, mentionSpan.nextSibling);

            const newRange = document.createRange();
            newRange.setStartAfter(spaceNode);
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);
        } else {
            const mentionSpan = createMentionSpan(user);

            range.deleteContents();
            range.insertNode(mentionSpan);

            const spaceNode = document.createTextNode(' ');
            mentionSpan.parentNode?.insertBefore(spaceNode, mentionSpan.nextSibling);

            const newRange = document.createRange();
            newRange.setStart(spaceNode, 1);
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);
        }

        const newText = htmlToText(editorRef.current.innerHTML);
        onChange({ target: { value: newText } });
        updateMentions();
    }, [onChange, htmlToText, updateMentions]);

    const insertRoomMention = useCallback((room: RoomMentionTarget) => {
        if (!editorRef.current) return;

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        const caretPosition = resolveCaretTextPosition(range.startContainer, range.startOffset);
        const roomMatch = getRoomMentionQueryMatch(caretPosition);

        const roomSpan = createRoomMentionSpan(room);

        if (roomMatch && caretPosition) {
            // The match may open on the whitespace that let the trigger fire;
            // only the "#" and what was typed after it get replaced, so the
            // space before the pill survives.
            const typedLength = 1 + roomMatch[1].length;
            const replaceRange = document.createRange();
            replaceRange.setStart(caretPosition.node, Math.max(0, caretPosition.offset - typedLength));
            replaceRange.setEnd(caretPosition.node, caretPosition.offset);
            replaceRange.deleteContents();
            replaceRange.insertNode(roomSpan);
        } else {
            range.deleteContents();
            range.insertNode(roomSpan);
        }

        const spaceNode = document.createTextNode(' ');
        roomSpan.parentNode?.insertBefore(spaceNode, roomSpan.nextSibling);

        const newRange = document.createRange();
        newRange.setStartAfter(spaceNode);
        newRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(newRange);

        const newText = htmlToText(editorRef.current.innerHTML);
        onChange({target: {value: newText}});
        updateMentions();
    }, [onChange, htmlToText, updateMentions]);

    const getSelectionOffsets = useCallback((): { start: number; end: number } | null => {
        if (!editorRef.current) return null;
        return getPlainTextSelection(editorRef.current);
    }, []);

    const applyTextChange = useCallback((text: string, caretOffset: number) => {
        if (!editorRef.current) return;

        isUserInputRef.current = true;
        editorRef.current.innerHTML = DOMPurify.sanitize(textToHtml(text));

        const position = plainOffsetToDomPosition(editorRef.current, caretOffset);
        const selection = window.getSelection();
        if (selection) {
            const range = document.createRange();
            range.setStart(position.node, position.offset);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
        }

        updateMentions();
        onChange({ target: { value: text } });
    }, [textToHtml, updateMentions, onChange]);

    useImperativeHandle(ref, () => ({
        insertMention,
        insertRoomMention,
        focus: () => editorRef.current?.focus(),
        getSelectionOffsets,
        applyTextChange
    }), [insertMention, insertRoomMention, getSelectionOffsets, applyTextChange]);

    useEffect(() => {
        if (editorRef.current && !isComposing && !isUserInputRef.current) {
            const currentText = htmlToText(editorRef.current.innerHTML);
            if (currentText !== value) {
                editorRef.current.innerHTML = DOMPurify.sanitize(textToHtml(value));
            }
        }
        isUserInputRef.current = false;
    }, [value, textToHtml, htmlToText, isComposing]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        if (!editorRef.current) return;

        // Call parent onKeyDown handler if provided
        if (onKeyDown) {
            onKeyDown(e);
        }

        if ((e.key === 'v' || e.key === 'V') && (e.ctrlKey || e.metaKey) && e.shiftKey) {
            forcePlainPasteRef.current = true;
        }

        if (e.key === 'Backspace' || e.key === 'Delete') {
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);

                if (!range.collapsed) {
                    const selectedContent = range.cloneContents();
                    const mentionsInSelection = selectedContent.querySelectorAll('.mention');

                    if (mentionsInSelection.length > 0) {
                        setTimeout(() => {
                            cleanupEditor();
                            ensureCleanCursor();
                        }, 0);
                        return;
                    }
                }

                if (e.key === 'Backspace') {
                    const container = range.startContainer;
                    const parent = container.parentNode as HTMLElement;

                    if (parent?.classList?.contains('mention') && range.startOffset === 0) {
                        e.preventDefault();
                        parent.remove();
                        return;
                    }
                }

                setTimeout(() => {
                    cleanupEditor();
                    ensureCleanCursor();
                }, 0);
            }
        }
    }, [cleanupEditor, ensureCleanCursor, onKeyDown]);

    const handleInput = useCallback((e: React.FormEvent<HTMLDivElement>) => {
        if (isComposing) return;

        isUserInputRef.current = true;

        cleanupEditor();
        ensureCleanCursor();
        updateMentions();

        const newText = htmlToText(e.currentTarget.innerHTML);
        onChange({ target: { value: newText } });

        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const caretPosition = resolveCaretTextPosition(range.startContainer, range.startOffset);
            const mentionMatch = getMentionQueryMatch(caretPosition);

            if (onMentionTrigger) {
                onMentionTrigger(mentionMatch);
            }

            if (onRoomMentionTrigger) {
                onRoomMentionTrigger(getRoomMentionQueryMatch(caretPosition));
            }
        }
    }, [onChange, htmlToText, onMentionTrigger, onRoomMentionTrigger, isComposing, cleanupEditor, ensureCleanCursor, onMentionsChange]);

    const lastReportedRangeRef = useRef<{
        startNode: Node;
        startOffset: number;
        endNode: Node;
        endOffset: number;
    } | null>(null);

    const reportSelection = useCallback(() => {
        if (!onSelectionChange || !editorRef.current) return;

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) {
            const wasAlreadyEmpty = lastReportedRangeRef.current === null;
            lastReportedRangeRef.current = null;
            if (!wasAlreadyEmpty) {
                onSelectionChange(null);
            }
            return;
        }

        const range = selection.getRangeAt(0);
        const last = lastReportedRangeRef.current;
        if (
            last &&
            last.startNode === range.startContainer &&
            last.startOffset === range.startOffset &&
            last.endNode === range.endContainer &&
            last.endOffset === range.endOffset
        ) {
            return;
        }

        lastReportedRangeRef.current = {
            startNode: range.startContainer,
            startOffset: range.startOffset,
            endNode: range.endContainer,
            endOffset: range.endOffset,
        };

        onSelectionChange(range.collapsed ? null : getPlainTextSelection(editorRef.current));
    }, [onSelectionChange]);

    useEffect(() => {
        if (!onSelectionChange) return;
        document.addEventListener('selectionchange', reportSelection);
        return () => document.removeEventListener('selectionchange', reportSelection);
    }, [onSelectionChange, reportSelection]);

    const handleKeyUp = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        reportSelection();
        forcePlainPasteRef.current = false;

        if (onKeyUp && !isComposing) {
            const syntheticEvent = {
                ...e,
                target: {
                    ...e.target,
                    value: htmlToText(e.currentTarget.innerHTML)
                }
            };
            onKeyUp(syntheticEvent as React.KeyboardEvent<HTMLDivElement> & { target: { value: string } });
        }
    }, [onKeyUp, htmlToText, isComposing, reportSelection]);

    const handleMouseUp = useCallback(() => {
        reportSelection();
    }, [reportSelection]);

    const handleBlur = useCallback(() => {
        forcePlainPasteRef.current = false;
    }, []);

    const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
        const forcePlain = forcePlainPasteRef.current;
        forcePlainPasteRef.current = false;

        const items = Array.from(e.clipboardData.items);
        const imageFiles: File[] = [];

        items.forEach(item => {
            if (item.type.startsWith('image/')) {
                const file = item.getAsFile();
                if (file) {
                    imageFiles.push(file);
                }
            }
        });

        const text = e.clipboardData.getData('text/plain');
        const html = e.clipboardData.getData('text/html') || undefined;
        const isSpreadsheetClipboard = html ? looksLikeSpreadsheetHtml(html) : text.includes('\t');
        const preferTextOverImages = text.length > 0 && (forcePlain || codeBlockOpen || isSpreadsheetClipboard);

        if (imageFiles.length > 0 && onPasteFiles && !preferTextOverImages) {
            e.preventDefault();
            onPasteFiles(imageFiles);
            return;
        }

        e.preventDefault();

        if (forcePlain || codeBlockOpen) {
            document.execCommand('insertText', false, text);
            return;
        }

        const converted = convertPaste({html, text});
        let insertText = isSpreadsheetClipboard && converted.kind === 'plain'
            ? converted.text.replace(/\r?\n$/, '')
            : converted.text;

        if (editorRef.current) {
            const currentText = domToPlainText(editorRef.current);
            const selection = getPlainTextSelection(editorRef.current) ?? {start: currentText.length, end: currentText.length};

            if (insertText.startsWith('```')) {
                const atLineStart = selection.start === 0 || currentText[selection.start - 1] === '\n';
                if (!atLineStart) {
                    insertText = '\n' + insertText;
                }
                if (!insertText.endsWith('\n')) {
                    insertText = insertText + '\n';
                }
            }

            if (converted.kind === 'table' && converted.tableSource === 'text' && onPlainTableInserted) {
                onPlainTableInserted({
                    prefix: currentText.slice(0, selection.start),
                    insertedText: insertText,
                    suffix: currentText.slice(selection.end),
                    originalText: normalizeLineEndings(text),
                });
            }
        }

        document.execCommand('insertText', false, insertText);
    }, [onPasteFiles, codeBlockOpen, onPlainTableInserted]);

    return (
        <div
            id={"mention-text-input"}
            ref={editorRef}
            contentEditable={!disabled}
            suppressContentEditableWarning={true}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            onMouseUp={handleMouseUp}
            onPaste={handlePaste}
            onBlur={handleBlur}
            onCompositionStart={() => setIsComposing(true)}
            onCompositionEnd={() => setIsComposing(false)}
            className={`resize-none text-sm w-full outline-none border-none ring-0 focus:border-none focus:ring-0 p-2 max-h-[320px] overflow-y-auto${codeBlockOpen ? ' font-mono' : ''}`}
            style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
            }}
            data-placeholder={!value ? placeholder : ''}
        />
    );
});

MentionTextInput.displayName = 'MentionTextInput';

export default MentionTextInput;
